// Asking in context (wave 13, w13-notify): the moment a notification is worth it, never at first visit. On the web
// that's Going on Events: "Remind you 15 minutes before?". The answer is saved in accounts (the eventReminder switch,
// and the ask marked answered), so a no isn't asked again here or in the app. Friends asks about messages in the
// app (launcher/src/ui/notify.rs), where desktop notifications can actually reach you.

import { h } from "./dom.ts";
import { answer, prefsOf, shouldAsk, type Ask } from "./notify.ts";
import { getFullMe } from "./pages/me-kit.ts";
import { signedIn } from "./session.ts";

let styled = false;
function askStyles(): void {
  if (styled) return;
  styled = true;
  document.head.append(
    h(
      "style",
      { id: "notify-ask-styles" },
      `
.nt-ask { grid-column: 1 / -1; display: flex; align-items: center; gap: 10px 14px; flex-wrap: wrap; margin-top: 12px; padding: 12px 14px; border-radius: 14px; background: color-mix(in srgb, var(--z-ink, #f2f2f4) 7%, transparent); border: 1px solid color-mix(in srgb, var(--z-ink, #f2f2f4) 14%, transparent); color: var(--z-ink, var(--text)); }
.nt-ask p { margin: 0; flex: 1 1 220px; font-weight: 650; line-height: 1.4; }
.nt-ask p small { display: block; font-weight: 400; color: var(--z-dim, var(--dim)); font-size: .8125rem; margin-top: 2px; }
.nt-ask-btns { display: flex; gap: 8px; flex: 1 1 auto; justify-content: flex-end; }
@media (max-width: 560px) { .nt-ask-btns { flex-basis: 100%; justify-content: flex-start; } }
.nt-ask button { min-height: 40px; padding: 0 16px; border-radius: 999px; font: inherit; font-weight: 700; cursor: pointer; border: 1px solid color-mix(in srgb, var(--z-ink, #f2f2f4) 30%, transparent); background: transparent; color: inherit; }
.nt-ask button.yes { background: var(--z-accent, var(--ruby)); border-color: var(--z-accent, var(--ruby)); color: var(--z-bg, #121216); }
.nt-ask button:focus-visible { outline: 2px solid var(--z-accent, var(--ruby)); outline-offset: 2px; }
.nt-ask[data-done] { background: transparent; border-color: transparent; padding: 0; }
.nt-ask[data-done] p { font-weight: 400; color: var(--z-dim, var(--dim)); }
`,
    ),
  );
}

const COPY: Record<Ask, { q: string; why: string; yes: string; no: string; saidYes: string; saidNo: string }> = {
  eventReminder: {
    q: "Remind you 15 minutes before?",
    why: "In your inbox, and on your desktop if you have the app.",
    yes: "Remind me",
    no: "No thanks",
    saidYes: "We'll remind you 15 minutes before. Change it any time in Settings.",
    saidNo: "No reminders. You'll still see it in your inbox when it starts.",
  },
  friendMessages: {
    q: "Tell you when a friend messages?",
    why: "One line per friend. We never show the message itself.",
    yes: "Tell me",
    no: "Not now",
    saidYes: "We'll tell you when a friend messages.",
    saidNo: "We won't. You can turn it on in Settings.",
  },
};

/**
 * Puts the ask after `anchor` when the player is signed in and hasn't answered it before. Answers once; the box then
 * says what happens and points at Settings.
 */
export async function offerAsk(anchor: Element, ask: Ask): Promise<boolean> {
  if (!signedIn()) return false;
  if (anchor.parentElement?.querySelector(`.nt-ask[data-ask="${ask}"]`)) return true;
  let prefs;
  try {
    prefs = prefsOf((await getFullMe()).notify);
  } catch {
    return false;
  }
  if (!shouldAsk(prefs, ask)) return false;
  askStyles();
  const c = COPY[ask];
  const text = h("p", null, c.q, h("small", null, c.why));
  const say = async (yes: boolean) => {
    box.querySelectorAll("button").forEach((b) => b.setAttribute("disabled", ""));
    try {
      await answer(ask, yes);
      box.setAttribute("data-done", "");
      btns.remove();
      text.textContent = yes ? c.saidYes : c.saidNo;
    } catch {
      box.querySelectorAll("button").forEach((b) => b.removeAttribute("disabled"));
      text.textContent = "Couldn't save that. Try again?";
    }
  };
  const btns = h(
    "div",
    { class: "nt-ask-btns" },
    h("button", { type: "button", class: "yes", onclick: () => say(true) }, c.yes),
    h("button", { type: "button", onclick: () => say(false) }, c.no),
  );
  const box = h("div", { class: "nt-ask", role: "group", "aria-label": c.q, "data-ask": ask }, text, btns);
  anchor.after(box);
  // a status line, so a screen reader hears the question appear and the answer land
  text.setAttribute("role", "status");
  return true;
}

/** Takes an unanswered ask away again (the player undid what it was about, e.g. not going after all). */
export function withdrawAsk(scope: Element | null, ask: Ask): void {
  scope?.querySelectorAll(`.nt-ask[data-ask="${ask}"]:not([data-done])`).forEach((e) => e.remove());
}
